/**
 * @author Красильников Даниил <dkrasilnikov@gmail.com>
 * @link https://t.me/krasilneg
 * 
 * @author Колесник Данил <90.kalias.3@gmail.com>
 * @link https://t.me/kalias_90
 */

import React, { ReactNode, useCallback } from 'react';
import { InlineMath } from 'react-katex';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';

import { Attribute, AttributeGroup } from '@bimdata/definitions/types';
import { FolderTreeNode, FolderTreeNodeStats } from '@bimdata/definitions/dto';

import Scrollable from '@/components/Scrollable';
import Caption from '@/components/Span.js';
import { ElementDetails } from '@bimdata/viewer-react';
import { RootState, useAppSelector } from '@/state/store';
import { saveExpandedAttributeGroups } from '@/actions/client';
import { ModelData } from '@/state/types/model-data';
import ViewerWindowContentList from './ViewerWindowContentList.js';
import t from '@/utils/translate';
import { strToHighlightRegExp } from '@/components/Highlighter.js';
import { createSelector } from '@reduxjs/toolkit';
import { Logger } from '@bimdata/client';

type Props = {
  element: ElementDetails | null;
  onClose?: () => void;
  onSelectClaim?: (filter: any) => void;
  selectionLength: number;
  filter: string | null;
};

const ACCURACY = 100000;

const narrowUnits = (units?: string | null): string => {
  switch (units) {
    case 'GRAMM':
    case 'GRAMMS': return 'GRAMMS';
    case 'KILOGRAMM':
    case 'KILOGRAMMS': return 'KILOGRAMMS';
    case 'TON':
    case 'TONS': return 'TONS';
    case 'NEWTON':
    case 'NEWTONS': return 'NEWTONS';
    case 'NEWTON_PER_METER':
    case 'NEWTONS_PER_METER': return 'NEWTONS_PER_METER';
    case 'NEWTON_PER_SQUARE_METER':
    case 'NEWTONS_PER_SQUARE_METER': return 'NEWTONS_PER_SQUARE_METER';
    case 'MILLIMETER':
    case 'MILLIMETERS': return 'MILLIMETERS';
    case 'SANTIMETER':
    case 'SANTIMETERS': return 'SANTIMETERS';
    case 'DECIMETER':
    case 'DECIMETERS': return 'DECIMETERS';
    case 'METER':
    case 'METERS': return 'METERS';
    case 'RUR':
    case 'RUB':
    case 'RUBLE':
    case 'CURRENCY': return 'CURRENCY';
    case 'KILOJOULES_PER_KELVIN': return 'KILOJOULES_PER_KELVIN';
    case 'KILOJOULE_PER_SQUARE_METER_KELVIN':
    case 'KILOJOULES_PER_SQUARE_METER_KELVIN': return 'KILOJOULES_PER_SQUARE_METER_KELVIN';
    case 'WATT_PER_SQUARE_METER_KELVIN':
    case 'WATTS_PER_SQUARE_METER_KELVIN': return 'WATTS_PER_SQUARE_METER_KELVIN';
    case 'SQUARE_METER_KELVIN_PER_WATT':
    case 'SQUARE_METERS_KELVINS_PER_WATT':
    case 'SQUARE_METER_KELVINS_PER_WATT': return 'SQUARE_METER_KELVINS_PER_WATT';
    case 'DEGREE':
    case 'DEGREES': return 'DEGREES';
    case 'SLOPE_DEGREE':
    case 'SLOPE_DEGREES': return 'SLOPE_DEGREES';
    case 'SQUARE_METER':
    case 'SQUARE_METERS': return 'SQUARE_METERS';
    case 'CUBIC_METER':
    case 'CUBIC_METERS': return 'CUBIC_METERS';
    default: return '';
  }
}

const unit = (units?: string | null): ReactNode | string => {
  switch (narrowUnits(units)) {
    case 'GRAMMS': return <>&nbsp;{t('units.mass.g')}</>;
    case 'KILOGRAMMS': return <>&nbsp;{t('units.mass.kg')}</>;
    case 'TONS': return <>&nbsp;{t('units.mass.t')}</>;
    case 'NEWTONS': return <>&nbsp;{t('units.force.n')}</>;
    case 'NEWTONS_PER_METER': return <>&nbsp;<InlineMath math={`${t('units.force.n')} \over ${t('units.length.m')}`} /></>;
    case 'NEWTONS_PER_SQUARE_METER': return <>&nbsp;<InlineMath math={`${t('units.force.n')} \over {${t('units.length.m')}^2}`} /></>;
    case 'MILLIMETERS': return <>&nbsp;{t('units.length.mm')}</>;
    case 'SANTIMETERS': return <>&nbsp;{t('units.length.sm')}</>;
    case 'DECIMETERS': return <>&nbsp;{t('units.length.dm')}</>;
    case 'METERS': return <>&nbsp;{t('units.length.m')}</>;
    case 'CURRENCY': return <>&nbsp;{t('units.money.rub')}</>;
    case 'KILOJOULES_PER_KELVIN': return <>&nbsp;<InlineMath math={`${t('units.energy.KJ')} \over ${t('units.temperature.K')}}`} /></>
    case 'KILOJOULES_PER_SQUARE_METER_KELVIN': return <>&nbsp;<InlineMath math={`${t('units.energy.KJ')} \over {{t('units.length.m')}^2 \cdot ${t('units.temperature.K')}}`} /></>;
    case 'WATTS_PER_SQUARE_METER_KELVIN': return <>&nbsp;<InlineMath math={`${t('units.power.W')} \over {${t('units.length.m')}^2 \cdot ${t('units.temperature.K')}}`} /></>;
    case 'SQUARE_METER_KELVINS_PER_WATT': return <>&nbsp;<InlineMath math={`{${t('units.length.m')}^2 \cdot ${t('units.temperature.K')}} \over ${t('units.power.W')}`} /></>;
    case 'DEGREES': return <>&nbsp;&deg;</>;
    case 'SLOPE_DEGREES': return <>&nbsp;&permil;</>;
    case 'SQUARE_METERS': return <>&nbsp;{t('units.length.m')}<sup>2</sup></>;
    case 'CUBIC_METERS': return <>&nbsp;{t('units.length.m')}<sup>3</sup></>;
    default: return '';
  }
};

const format = (v: string | number | null, re: RegExp, filter: string, units?: string): ReactNode | string => {
  if (typeof v === 'string' && filter.length && re) {
    const m = v.match(re);
    if (m) {
      const part1 = v.substring(0, m.index);
      const part2 = v.substring(m.index!, m.index! + filter.length);
      const part3 = v.substring(m.index! + filter.length);
      return <>{part1}<span style={{ backgroundColor: '#ffef5b' }}>{part2}</span>{part3}</>;
    }
  }
  return typeof v == 'number'
    ? <>{Math.round((v + Number.EPSILON) * ACCURACY) / ACCURACY}{units ? unit(units) : ''}</>
    : `${v || ''}`;
};

const NotFound = styled('div')(({ color = '#42526E' }: { color?: string }) => ({
  background: '#FAFAFA',
  color,
  padding: '10px 68px',
  borderRadius: 6,
  border: '1px solid #C1C7D0',
  fontSize: '13px',
  textAlign: 'center',
}));

const EMPTY_GROUPS = {};

const ElementProperties = ({ filter, element, onClose, selectionLength }: Props) => {
  const { project, pClassification, models } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.projects.current?.code,
        (state: RootState) => state.projects.current?.classification,
        (state: RootState) => state.projects.current?.models,
      ],
      (project, pClassification, models) => ({
        project,
        pClassification,
        models,
      })
    )
  );  

  const CLASS_LABELS: string[] = [t('bim.term.category'), t('bim.term.family'), t('bim.term.type')];

  const opened = useAppSelector((state) => state.authentication.clientState.expandedAttributeGroups[project || ''] || EMPTY_GROUPS);

  const classification: { name: string, elements: number }[] = [];

  const modelMap: Map<number, ModelData> = new Map();
  const classificationMap: Map<number, FolderTreeNodeStats> = new Map();

  const fre = !!filter ? strToHighlightRegExp(filter) : undefined;
  const groups = ((element && element.attributes) || ([] as AttributeGroup[]))
    .reduce<AttributeGroup[]>((groups, v) => {
      const g = groups.find((g) => g.name === v.name);
      if (g) {
        g.attributes.push(...v.attributes);
      } else {
        groups.push(v);
      }
      return groups;
    }, [])
    .map(({ code, name, attributes }) => {
      const attrs: Attribute[] = !filter
        ? attributes
        : attributes.filter(
          ({ name, value }) => !fre || fre.test(name) || (typeof value === 'string' && fre.test(value))
        );
      return {
        code,
        name,
        opened: opened[code] === true,
        list: attrs.reduce<Attribute[]>((list, a) => {
          const existing = list.filter((item) => item.name === a.name || item.code === a.code);
          if (!existing.length) {
            list.push(a);
          } else {
            const withSameUnit = existing.find((item) => narrowUnits(item.unit) === narrowUnits(a.unit));
            if (withSameUnit && (typeof withSameUnit.value == 'undefined' || withSameUnit.value === '' || withSameUnit.value === null)) {
              withSameUnit.value = a.value;
            } else if (!withSameUnit || (a.value && withSameUnit.value !== a.value)) {
              const withSameCode = existing.find((item) => item.code === a.code);
              list.push(!withSameCode ? a : { ...a, code: `${a.code}_${list.length}` });
            }
          }
          return list;
        }, []).map(({ code, name, value, unit }) => ({
          code,
          name: format(name, fre!, filter || ''),
          value: format(value, fre!, filter || '', unit),
        }))
      };
    });

  const model = useCallback((id: number): ModelData | null => {
    if (modelMap.has(id)) {
      return modelMap.get(id)!;
    }
    for (const s of models || []) {
      for (const m of s.models || []) {
        if (!modelMap.has(m.id)) {
          modelMap.set(m.id, m);
        }
        if (m.id === id) return m;
      }
    }
    return null;
  }, [models]);

  const folder = useCallback((id: number, list?: FolderTreeNodeStats[]): FolderTreeNodeStats | null => {
    if (classificationMap.has(id)) {
      return classificationMap.get(id)!;
    }
    for (const n of list || pClassification || []) {
      if (!classificationMap.has(n.id)) {
        classificationMap.set(n.id, n);
      }
      if (n.id === id) return n;
      if (n.childNodes && n.childNodes.length) {
        const ch = folder(id, n.childNodes);
        if (ch) return ch;
      }
    }
    return null;
  }, [pClassification]);

  const traverse = (n: FolderTreeNode | FolderTreeNodeStats) => {
    const stats = (n as FolderTreeNodeStats).stats || folder(n.id)?.stats || [];
    classification.push({
      name: n.name,
      elements: stats!.reduce((s, m) => s + (m.model === element!.model ? m.elements : 0), 0)
    });
    if (n.childNodes.length) {
      traverse(n.childNodes[0]);
    }
  };

  const switchGroup = useCallback((code: string, value: boolean) => {
    if (project) {
      saveExpandedAttributeGroups(
        project,
        {
          ...opened,
          [code]: value
        }
      ).catch((e) => {
        Logger.error(e);
      });
    }
  }, [project]);
  
  if (element && element.classification.length) {
    traverse(element.classification[0]);
  }

  return (
    <Scrollable id="propertiesList">
      {!selectionLength && (
        <NotFound>{t('viewer.hint.selectElement')}</NotFound>
      )}
      {(selectionLength > 1) && (
        <NotFound>
          <p>{t('viewer.hint.selectOneElement', true)}</p>
          <p>{t('viewer.hint.multiselect', true)}</p>
        </NotFound>
      )}
      {(selectionLength === 1) && <>
        {(filter === null) &&
        (<div style={{
          backgroundColor: '#EBECF0',
          borderRadius: '6px',
          overflow: 'hidden',
          fontSize: '13px',
          fontWeight: 400,
          lineHeight: '15px',
          color: '#091E42',
        }}>
          <div style={{
              padding: '6px 10px',
              backgroundColor: '#FAFAFA',
              marginBottom: '1px',
            }}
          >
            <Table size="small" sx={{
              tableLayout: 'fixed',
              '& td': {
                padding: 0,
                height: '16px',
              }
            }}>
              <TableBody>
                <TableRow key="section">
                  <td style={{
                      width: '130px',
                      fontWeight: 700,
                    }}
                  >
                    {t('bim.term.section')}
                  </td>
                  <td colSpan={2}>
                    {
                    element
                      ? (<Caption>{model(element.model)?.section}</Caption>)
                      : (<Skeleton variant="rounded" width={50} />)
                    }
                  </td>
                </TableRow>
                <TableRow key="model">
                  <td style={{
                      width: '130px',
                      fontWeight: 700,
                    }}
                  >
                    {t('bim.term.model')}
                  </td>
                  <td colSpan={2}>
                    {
                      element
                      ? (<Caption>{model(element.model)?.name}</Caption>)
                      : (<Skeleton variant="rounded" width="100%" />)
                    }
                  </td>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <div style={{
              padding: '6px 10px',
              backgroundColor: '#FAFAFA',
            }}
          >
            <Table size="small" sx={{
              tableLayout: 'fixed',
              '& td': {
                padding: 0,
                height: '16px',
              }
            }}>
              <TableBody>
                {
                  CLASS_LABELS.map((label: string, i: number) => {
                    return (
                    <TableRow key={`class${i}`}>
                      <td style={{
                          width: '130px',
                          fontWeight: 700,
                        }}
                      >
                        {label}
                      </td>
                      <td>
                        {
                          element
                          ? (<Caption>{classification[i]?.name}</Caption>)
                          : (<Skeleton variant="rounded" width="100%" />)
                        }
                      </td>
                      <td style={{
                          textAlign: 'right',
                          width: '60px',
                          color: '#6B778C'
                        }}
                      >
                        {
                          element
                          ? classification[i]?.elements
                          : (<Skeleton variant="rounded" width="100%" />)
                        }
                      </td>
                    </TableRow>
                    );
                  })
                }
              </TableBody>
            </Table>
          </div>
        </div>)}
        {!element && (
          <Skeleton variant="rounded" width="100%" height={200} />
        )}
        {element && !groups.filter((v) => v.list.length).length && (
          <NotFound color="#C1C7D0">{t('viewer.message.nothingFound')}</NotFound>
        )}
        {element && !!groups.length && (
          <ViewerWindowContentList
              groups={groups}
              forcedOpenAll={!!filter}
              switchGroup={switchGroup}
            />
        )}
      </>}
    </Scrollable>
  );
};

export default ElementProperties;
